import { useState, useEffect, useCallback, useMemo } from 'react';
import { dealerAPI } from '@/lib/supabase/dealers';

export interface SimpleDealerData {
  seller_name: string;
  seller_address: string;
  dealer_city: string;
  dealer_region: string;
  dealer_phone: string;
  dealer_inventory_url: string;
  dealer_type: string;
  seller_latitude?: number;
  seller_longitude?: number;
  total_recalls: number;
  unique_campaigns: number;
  is_included: boolean;
}

export interface DealerWithPreferences extends SimpleDealerData {
  notes?: string;
  status: 'included' | 'excluded';
}

type DealerPreference = {
  is_included?: boolean;
  notes?: string;
  created_at?: string | undefined;
  modified_at?: string | undefined;
  created_by?: string | undefined;
  modified_by?: string | undefined;
};

export const useDealerManagementSimple = (userId?: string) => {
  const [rawData, setRawData] = useState<SimpleDealerData[]>([]);
  const [dealerPreferences, setDealerPreferences] = useState<Record<string, DealerPreference>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(true);

  // Check online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    setIsOnline(navigator.onLine);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load preferences from localStorage as fallback
  const loadFromLocalStorage = useCallback((): Record<string, DealerPreference> => {
    try {
      const savedPreferences = localStorage.getItem('dealerPreferences');
      if (savedPreferences) {
        return JSON.parse(savedPreferences) as Record<string, DealerPreference>;
      }
    } catch {
      // Fall through to empty preferences when localStorage payload is invalid.
    }
    return {};
  }, []);

  // Save to localStorage as backup
  const saveToLocalStorage = useCallback((preferences: Record<string, DealerPreference>) => {
    try {
      localStorage.setItem('dealerPreferences', JSON.stringify(preferences));
    } catch {
      // Best effort only; in-memory state still reflects latest changes.
    }
  }, []);

  // Load data from tenant_dealers_view instead of extracting from MV
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        if (!userId || !isOnline) {
          // Fallback to localStorage if offline or no user
          const preferences = loadFromLocalStorage();
          setDealerPreferences(preferences);
          setLoading(false);
          setRawData([]);
          return;
        }

        // Get dealers with preferences from tenant_dealers_view
        const dealers = await dealerAPI.getDealersWithPreferences(userId);

        // Transform dealers to SimpleDealerData format for compatibility
        const transformedData: SimpleDealerData[] = dealers.map(dealer => ({
          seller_name: dealer.dealer_name,
          seller_address: dealer.seller_address || '',
          dealer_city: dealer.seller_city || '',
          dealer_region: dealer.seller_region || '',
          dealer_phone: dealer.phone || '',
          dealer_inventory_url: '',
          dealer_type: dealer.dealer_type || '',
          total_recalls: dealer.vehicles,
          unique_campaigns: dealer.campaigns,
          is_included: dealer.is_included ?? true,
          ...(dealer.seller_latitude !== undefined
            ? { seller_latitude: dealer.seller_latitude }
            : {}),
          ...(dealer.seller_longitude !== undefined
            ? { seller_longitude: dealer.seller_longitude }
            : {})
        }));

        setRawData(transformedData);

        // Extract preferences from dealers data (notes are already in dealer object)
        const preferences: Record<string, DealerPreference> = {};
        dealers.forEach(dealer => {
          if (dealer.notes !== undefined || dealer.is_included !== undefined) {
            preferences[dealer.dealer_name] = {
              is_included: dealer.is_included ?? true,
              notes: dealer.notes || ''
            };
          }
        });
        setDealerPreferences(preferences);
        saveToLocalStorage(preferences);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load dealer data');

        // Fallback to localStorage on error
        const preferences = loadFromLocalStorage();
        setDealerPreferences(preferences);
        setRawData([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userId, isOnline, loadFromLocalStorage, saveToLocalStorage]);

  // Get dealer preference
  const getDealerPreference = useCallback(
    (dealerName: string): DealerPreference => {
      if (!dealerName) return { is_included: true };

      // Normalize dealer name for matching (trim and case-insensitive)
      const normalizedName = dealerName.trim();

      // Try exact match first
      const exactMatch = dealerPreferences[normalizedName];
      if (exactMatch) {
        return exactMatch;
      }

      // Try case-insensitive match
      const lowerName = normalizedName.toLowerCase();
      const matchingKey = Object.keys(dealerPreferences).find(
        key => key.trim().toLowerCase() === lowerName
      );

      if (matchingKey) {
        return dealerPreferences[matchingKey] ?? { is_included: true };
      }

      // Default to included if no preference found
      return { is_included: true };
    },
    [dealerPreferences]
  );

  // Transform rawData (SimpleDealerData) to DealerWithPreferences
  // rawData now comes from tenant_dealers_view via dealerAPI
  const dealers = useMemo((): DealerWithPreferences[] => {
    if (!rawData.length) {
      return [];
    }

    // rawData is already in SimpleDealerData format from dealerAPI
    return rawData.map((dealerData: SimpleDealerData) => {
      const preference = getDealerPreference(dealerData.seller_name);
      return {
        ...dealerData,
        is_included: preference.is_included !== false,
        notes: preference.notes || '',
        status: preference.is_included !== false ? 'included' : 'excluded'
      };
    });
  }, [rawData, getDealerPreference]);

  // Get dealer statistics
  const stats = useMemo(() => {
    const totalDealers = dealers.length;
    const includedDealers = dealers.filter(d => d.is_included).length;
    const excludedDealers = totalDealers - includedDealers;
    const totalRecalls = dealers.reduce((sum, d) => sum + d.total_recalls, 0);
    const totalCampaigns = dealers.reduce((sum, d) => sum + d.unique_campaigns, 0);

    return {
      totalDealers,
      includedDealers,
      excludedDealers,
      totalRecalls,
      totalCampaigns
    };
  }, [dealers]);

  // Read-only: dealer settings are written only through set_dealer_config
  // (src/lib/supabase/dealerConfig.ts) from the Dealers screen.
  return {
    dealers,
    stats,
    loading,
    error
  };
};
